/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { Fragment, type ReactNode, useMemo } from "react";
import { Loader2 } from "lucide-react";
import type { Call } from "@/lib/api/calls";
import { useGetCallLogs } from "@/lib/api/calls";
import type { Note } from "@/lib/api/notes";
import { mergeNotesAndCallLogs } from "./merge-notes-and-call-logs";

interface NotesWithCallLogsProps {
  mentorshipId?: string;
  notes: Note[];
  renderNote: (note: Note) => ReactNode;
  renderCallLog: (callLog: Call) => ReactNode;
}

export function NotesWithCallLogs({
  mentorshipId,
  notes,
  renderCallLog,
  renderNote,
}: NotesWithCallLogsProps) {
  const { data: callLogs, isLoading: isCallLogsLoading } =
    useGetCallLogs(mentorshipId);

  const list = useMemo(
    () => mergeNotesAndCallLogs(notes, callLogs ?? []),
    [callLogs, notes],
  );

  return (
    <>
      {isCallLogsLoading ? (
        <div className="mt-4 flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        list.map((item) => {
          if (item.type === "note") {
            return <Fragment key={item.id}>{renderNote(item)}</Fragment>;
          }
          return <Fragment key={item.id}>{renderCallLog(item)}</Fragment>;
        })
      )}
    </>
  );
}
